/**
 * ClaimLineageBanner Component
 *
 * Compact banner showing parent/child claim relationships.
 * Fetches lineage from the org-scoped detail endpoint.
 * Renders above the CMS-1500 form in both edit and read-only views.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AccountTree as AccountTreeIcon,
  ArrowUpward as ParentIcon,
  CallSplit as ChildIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Chip,
  Link,
  Typography,
} from '@mui/material';

import { useBillingBasePath, useGetBillingClaimDetailQuery } from '../../store';

interface ClaimLineageBannerProps {
  claimId: string;
}

export const ClaimLineageBanner: React.FC<ClaimLineageBannerProps> = ({ claimId }) => {
  const navigate = useNavigate();
  const basePath = useBillingBasePath();

  const { data } = useGetBillingClaimDetailQuery(
    { claimId },
    { skip: !claimId },
  );

  const lineage = data?.lineage;
  const claim = data?.claim;

  // Nothing to show if no lineage relationships and not superseded
  const supersededBy = claim?.superseded_by_claim_id as string | undefined;
  if (!lineage && !supersededBy) return null;
  const hasParent = Boolean(lineage?.parent_claim_id);
  const hasChildren = lineage?.child_claim_ids && lineage.child_claim_ids.length > 0;
  if (!hasParent && !hasChildren && !supersededBy) return null;

  const claimType = claim?.claim_type as string | undefined;

  const handleNavigate = (targetClaimId: string) => {
    navigate(`${basePath}/claim/${targetClaimId}`);
  };

  return (
    <>
    {/* Superseded banner — separate from lineage, higher visual priority */}
    {supersededBy && (
      <Alert
        severity="warning"
        sx={{
          mb: 2,
          '& .MuiAlert-message': { width: '100%' },
        }}
      >
        <Typography variant="body2">
          A newer version of this claim was filed.{' '}
          <Link
            component="button"
            variant="body2"
            onClick={() => handleNavigate(supersededBy)}
            sx={{ fontWeight: 600, cursor: 'pointer' }}
          >
            View latest
          </Link>
        </Typography>
      </Alert>
    )}

    {/* Lineage relationships */}
    {(hasParent || hasChildren) && (
    <Alert
      severity="info"
      icon={<AccountTreeIcon fontSize="small" />}
      sx={{
        mb: 2,
        '& .MuiAlert-message': { width: '100%' },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Parent relationship */}
        {hasParent && lineage?.parent_claim_id && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ParentIcon sx={{ fontSize: 16, color: 'info.main' }} />
            <Typography variant="body2">
              This is a{' '}
              <Chip
                label={claimType === 'void' ? 'void' : 'correction'}
                size="small"
                color={claimType === 'void' ? 'error' : 'info'}
                sx={{ height: 18, fontSize: '0.7rem', fontWeight: 600, '& .MuiChip-label': { px: 0.5 } }}
              />
              {' '}of{' '}
              <Link
                component="button"
                variant="body2"
                onClick={() => handleNavigate(lineage.parent_claim_id as string)}
                sx={{ fontWeight: 600, cursor: 'pointer' }}
              >
                original claim
              </Link>
            </Typography>
          </Box>
        )}

        {/* Children relationships */}
        {hasChildren && lineage?.child_claim_ids && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <ChildIcon sx={{ fontSize: 16, color: 'info.main' }} />
            <Typography variant="body2">
              This claim has {lineage.child_claim_ids.length} {lineage.child_claim_ids.length === 1 ? 'correction/void' : 'corrections/voids'}:
            </Typography>
            {lineage.child_claim_ids.map((childId) => (
              <Link
                key={childId}
                component="button"
                variant="body2"
                onClick={() => handleNavigate(childId)}
                sx={{ fontWeight: 600, cursor: 'pointer', fontSize: '0.8rem' }}
              >
                {childId.slice(0, 8)}...
              </Link>
            ))}
          </Box>
        )}
      </Box>
    </Alert>
    )}
    </>
  );
};
